import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { JobType } from '../graphql/types';
import { useWorkflowWorkspace } from '../workspace/WorkflowWorkspace';
import IntakeBA from '../workspace/intake/IntakeBA';
import IntakeDD from '../workspace/intake/IntakeDD';
import ExtractBody from '../workspace/extract/ExtractBody';
import AnalyzeMode from '../workspace/analyze/AnalyzeMode';

/**
 * WorkflowJobPage - DD/BA Mode body hosted inside the Workspace.
 *
 * The Workspace container (`WorkflowWorkspace`) loads the job, keeps it fresh
 * (subscription + polling fallback) and renders the loading/error states, the
 * back link, the title and the Status_Pill. This component only picks the body
 * for the active Mode, read from `useWorkflowWorkspace()`:
 * - `mode=intake`: `IntakeDD` / `IntakeBA`
 * - `mode=extract`: `ExtractBody` (board `1a`)
 * - `mode=analyze`: `AnalyzeMode`, job-wide (board `3a`) or one loan (board
 *   `3b`) through the `loan` URL param
 *
 * The Rail (loans for DD, documents for BA) is the Workspace's, not part of any
 * body: `WorkflowWorkspace` shows it on Intake, Extract and Analyze alike.
 *
 * A locked or unavailable Mode never reaches this component: `WorkspaceRoute`
 * falls back to the default or first open Mode.
 *
 * Requirements: 3.1, 3.3, 9.1, 9.2, 9.3, 9.4, 9.6
 */

/**
 * Intake Mode body: `IntakeBA` for a Borrower Analysis loan, `IntakeDD` for a
 * Due Diligence job. Both come from the redesign (tasks 6.1, 6.2).
 */
const IntakeBody: React.FC = () => {
  const location = useLocation();
  const { job, jobType, updateJob, reportStatus, refreshData } = useWorkflowWorkspace();
  const onDataChanged = useCallback(() => void refreshData(), [refreshData]);
  if (jobType === JobType.BA) {
    return (
      <IntakeBA
        job={job}
        uploadBucket={location.state?.uploadBucket || undefined}
        onJobUpdate={updateJob}
        onCountsChange={reportStatus}
        onDataChanged={onDataChanged}
      />
    );
  }
  return <IntakeDD />;
};

const WorkflowJobPage: React.FC = () => {
  const { mode } = useWorkflowWorkspace();
  if (mode === 'extract') return <ExtractBody />;
  if (mode === 'analyze') return <AnalyzeMode />;
  return <IntakeBody />;
};

export default WorkflowJobPage;
